import {Injectable} from '@angular/core';

import {CmsApiService} from './cms-api.service';
import {ReferenceFilterService} from './reference-filter.service';
import {CommonsService} from './commons.service';
import {FieldParameters} from './definitions/field-parameters';
import {QueryParserService} from './query-parser.service';
import {FieldValueService} from './field-value.service';
import {HierarchicNode} from './definitions/hierarchic-objects';
import {MetaField} from './definitions/meta-field';
import {Reference} from './definitions/reference';
import {Option, OptionInfo} from './definitions/option-info';
import {SearchParameters} from './definitions/search-parameters';
import {SearchObject} from './definitions/search-object';
import {BaseModel} from './definitions/base-model';
import {UserCacheService} from './user-cache.service';
import {FieldActionParameters} from '../shared/field-action-parameters';
import {Inline} from './definitions/inline';
import {FieldActionService} from '../shared/field-action.service';
import {AConst} from './a-const.enum';
import {CurrentQuery} from './definitions/current-query';
import {ModelFactoryService} from './model-factory.service';
import {SolrFilterService} from './solr-filter.service';
import {LoggerService} from './logger.service';
import {ModelsService} from './models.service';
import {SearchParams} from './search-params';
import {ListRange} from '@angular/cdk/collections';
import {CmsQueueService} from './cms-queue.service';
import {SearchService} from "./search.service";
import {InlineViewService} from "./inline-view.service";
import {SearchReferenceService} from "./search-reference.service";
import {ValueOptionService} from "./value-option.service";
import {FieldInputType} from "./definitions/field-input-type.enum";

export interface GetParentResult {
  parentNode: HierarchicNode;
  existed: boolean;
}

export class SelectFieldOptionContainer {
  fieldParameters: FieldParameters;
  fieldKey: string;
  refProp: string;
  myFormControl;
  query: CurrentQuery;
  editFieldSelectQuery;
  temporaryFieldValueName: string;
  setQueryTimoutId;
}

@Injectable({
  providedIn: 'root'
})
export class OptionsService {

  constructor(private cms: CmsApiService,
              private logger: LoggerService,
              private referenceFilterSvc: ReferenceFilterService,
              private commons: CommonsService,
              private queryParser: QueryParserService,
              private fieldValueService: FieldValueService,
              private userCacheService: UserCacheService,
              private fieldActionService: FieldActionService,
              private modelFactory: ModelFactoryService,
              private modelsService: ModelsService,
              private searchService: SearchService,
              private solrFilter: SolrFilterService,
              private cmsQueue: CmsQueueService,
              private inlineViewService: InlineViewService,
              private searchReferenceService: SearchReferenceService,
              private valueOptionService: ValueOptionService) {
  }

  async searchOptions(params: SearchParams, range?: ListRange, options?: Option[]): Promise<Option[]> {
    const searchParams = await this.getSearchParams(params);
    searchParams.start = (range?.start) ? range.start : 0;
    const searchRes = await this.searchService.search(searchParams);
    let res: Option[];
    res = (options) ? options : [];

    if (searchRes.artifacts) {
      this.filterOptions(params.fieldParameters, searchRes.artifacts)
        .forEach((value, index) => {
          res[searchParams.start + index] = value;
        });
    }
    return res;
  }

  filterOptions(fieldParameters: FieldParameters, options: Array<SearchObject>) {
    let res: Option[] = this.commons.copy(options);
    const [arrayObject, arrayName] = this.getArrayObjectAndArrayName(fieldParameters);
    if (arrayObject) {
      const parentMetaField: MetaField = arrayObject.$$meta[arrayName];
      if (parentMetaField.inline) {
        const uniqueProps = parentMetaField.inline.unique_props;
        const uniqueValues = parentMetaField.inline.unique_values;
        if (uniqueProps && (uniqueProps.length === 1 || uniqueValues)) {
          res = this.filterUniqueOptions(arrayObject[arrayName], options, uniqueProps, uniqueValues);
        }
      }
    }
    return res;
  }

  async initSelect(params: SelectFieldOptionContainer, reference: Reference) {
    const metaField: MetaField = params.fieldParameters.field;
    if (metaField.field_type === AConst.ARRAY) {
      const inlineFieldName = this.getInlineFieldName(metaField);
      let [arrayObject, arrayFieldName] = this.getArrayObjectAndArrayName(params.fieldParameters);
      if (!this.fieldValueService.isSingleItemArray(params.fieldParameters)) {
        this.setQueryValue({value: ''}, params);
      } else {
        const value = this.getTextValue(params.fieldParameters);
        this.setQueryValue({value: value}, params);
      }
      params.temporaryFieldValueName = '$$' + inlineFieldName + '_value';
      if (!arrayObject[arrayFieldName]) {
        arrayObject[arrayFieldName] = [];
      }
      if (arrayObject[arrayFieldName].length > 0) {
        for (const option of arrayObject[arrayFieldName]) {
          await this.setIconAndAuthority(option, inlineFieldName, metaField, reference);
        }
      }
      this.removeEmptyRefArrayItems(params.fieldParameters);
    } else {
      const value = this.getTextValue(params.fieldParameters);
      this.setQueryValue({value: value}, params);
      params.temporaryFieldValueName = '$$' + metaField.name + '_value';
      await this.setIconAndAuthority(params.fieldParameters.object, metaField.name, metaField, reference);
    }
  }

  async optionsSelected(options: Option[], params: SelectFieldOptionContainer, reference: Reference) {
    let value;
    if (options && options.length) {
      if (params.fieldParameters.field.field_type !== 'array') {
        value = await this.setFieldValueFromOption(options[0], params, reference);
      } else {
        value = await this.addFieldArrayItemsFromOptions(options, params, reference);
      }
    }
    return value;
  }

  optionUnchecked(option, params: SelectFieldOptionContainer) {
    const optionId = option[params.refProp];
    const array = this.getArray(params.fieldParameters);
    const foundIndex = this.findFieldArrayItemIndex(optionId, params);
    if (foundIndex !== -1) {
      this.modelFactory.deleteArrayItem(array, foundIndex, params.fieldParameters.rootObject);
      const count = this.modelFactory.countArrayElements(array);
      params.myFormControl.setValue(count ? count : '');
      params.myFormControl.markAsDirty();
      option.$$isSelected = false;
    }
  }

  setQueryValue(queryInfo, params: SelectFieldOptionContainer) {
    if (params.setQueryTimoutId) {
      clearTimeout(params.setQueryTimoutId);
      params.setQueryTimoutId = 0;
    }
    params.setQueryTimoutId = setTimeout(() => {
      if (!params.myFormControl) {
        return
      }
      params.query.value = queryInfo.value;
      params.query.valueWithTag = queryInfo.value;
      if (queryInfo?.value && queryInfo.value.indexOf('<del>') !== -1) {
        params.query.value = params.query.value.replace('<del>', '').replace('</del>', '');
      }
      let setControlValue = true;
      if (params.fieldParameters.field.field_type === 'array') {
        setControlValue = false;
        const inline: Inline = params.fieldParameters.field.inline;
        if (inline && inline.inline_list && inline.inline_list.max_length === 1) {
          setControlValue = true;
        }
      }
      if (setControlValue) {
        params.myFormControl.setValue(queryInfo.value);
      }
    }, queryInfo.timeout ? queryInfo.timeout : 100);
  }

  clearField(params: SelectFieldOptionContainer) {
    this.setQueryValue({value: ''}, params);
    if (params.fieldParameters.field.field_type !== 'array') {
      this.setFieldValue(null, params.fieldParameters, params.fieldKey);
      this.setFieldTextValue('', params.fieldParameters, params.fieldKey);
    }
    if (params.temporaryFieldValueName) {
      params.fieldParameters.object[params.temporaryFieldValueName] = '';
    }
  }

  // Query based search into hierarchic objects which will attempt to build the parent nodes based on the search result
  async hierarchicQuerySearch(fieldParameters: FieldParameters, query: string, page, pageSize): Promise<HierarchicNode> {
    const reference: Reference = await this.getReference(fieldParameters);
    const params = {
      rows: pageSize,
      start: pageSize * page
    } as SearchParameters;
    const displayField = await this.getDisplayField(reference, 'artifact_name');
    const nodeDisplayField = await this.getNodeDisplayField(reference, 'name.name');
    let queryField: string;
    let queryValue: string;
    if (nodeDisplayField === 'code') {
      queryValue = query;
      queryField = displayField;
    } else {
      queryValue = this.parseQuery(query);
      queryField = 'name.name_upper';
    }
    params.sort = queryField;
    this.solrFilter.addFq(params, queryField, queryValue);
    this.solrFilter.addFq(params, 'object_type', reference.object_type);
    this.solrFilter.addFq(params, '-valid', false);
    const filterData = await this.referenceFilterSvc.generateRefFilterData(
      reference.ref_filter, true, fieldParameters);
    this.solrFilter.setFqFromObject(params, filterData);
    const searchResult = await this.searchService.search(params);
    const rootNode = new HierarchicNode();
    if (searchResult.artifacts.length) {
      const searchNodes: HierarchicNode[] = searchResult.artifacts.map(searchObj => this.searchObjectToNode(
        searchObj, false, nodeDisplayField));
      await this.putSearchNodesInHierarchy(rootNode, searchNodes, nodeDisplayField);
    }
    return rootNode;
  }

  async addHierarchicNodes(parentNode: HierarchicNode,
                           fieldParameters: FieldParameters,
                           reference: Reference,
                           page,
                           pageSize,
                           parentId: string,
                           level?: number): Promise<void> {
    const params = {
      sort: 'name.name_upper',
      rows: pageSize,
      start: pageSize * page
    } as SearchParameters;
    this.solrFilter.addFq(params, 'object_type', reference.object_type);
    this.solrFilter.addFq(params, '-valid', false);
    if (parentId) {
      this.solrFilter.addFq(params, 'parent_id', parentId);
    } else {
      if (level !== undefined) {
        this.solrFilter.addFq(params, 'level', 1);
      } else {
        this.logger.warn('Level must be set when parent_id is not set');
      }
    }
    const filterData = await this.referenceFilterSvc.generateRefFilterData(
      reference.ref_filter, true, fieldParameters);
    this.solrFilter.setFqFromObject(params, filterData);
    const searchResult = await this.searchService.search(params);
    if (searchResult.artifacts.length) {
      const displayField = await this.getDisplayField(reference, 'name.name');
      const searchNodes = searchResult.artifacts.map(searchObj => this.searchObjectToNode(
        searchObj, false, displayField));
      await this.addChildren(searchNodes, displayField);
      this.appendNodesAddViewMoreNode(parentNode, searchNodes, searchResult.search_count);
    }
  }

  async addChildren(parentNodes: HierarchicNode[], displayField: string) {
    const searchParameters = {
      rows: 10000,
      sort: 'name.name_upper',
    } as SearchParameters;
    this.solrFilter.addFq(searchParameters, 'parent_id', parentNodes.map(parentNode => parentNode.artifact_id));
    this.solrFilter.addFq(searchParameters, 'object_type', parentNodes[0].object_type);
    this.solrFilter.addFq(searchParameters, '-valid', false);
    const searchRes = await this.searchService.search(searchParameters);
    for (const parentNode of parentNodes) {
      const childSearchObjects = searchRes.artifacts.filter(searchObject => searchObject.parent_id === parentNode.artifact_id);
      const childNodes = childSearchObjects.map(childSearchObj => this.searchObjectToNode(childSearchObj, true, displayField));
      this.appendNodesAddViewMoreNode(parentNode, childNodes);
    }
  }


  async setGrandChildren(node: HierarchicNode, displayField: string) {
    const childNodes = node.children.filter(child => child.$$nodeType !== 'loadMoreNode');
    await this.addChildren(childNodes, displayField);
  }

  async setIconAndAuthority(item: BaseModel, fieldName: string, fieldInfo: MetaField, reference: Reference): Promise<void> {
    if (['model', 'template_model'].indexOf(item.meta_type) !== -1) {
      return;
    }
    let icon;
    const inlineView = await this.inlineViewService.getInlineViewForField(item.object_type, fieldInfo);
    if (inlineView) {
      icon = inlineView.icon;
    }
    const artifactId = item[fieldName];
    if (artifactId) {
      const params = {} as SearchParameters;
      this.solrFilter.addFq(params, 'artifact_id', artifactId);
      const res = await this.searchService.search(params);
      let art: Option;
      if (res.artifacts && res.artifacts.length) {
        art = <Option>res.artifacts[0];
        if (art.authority_id_value === 'KulturNav' || icon) {
          await this.setSelectedOptionName(reference, art, null);
          item['$$' + fieldName + '_value'] = art.$$name;
        } else {
          item['$$' + fieldName + '_value'] = this.fieldValueService.getValueCompanion(item, fieldName);
        }
      }
    }
  }

  setNodeIsSelectedById(parentNode: HierarchicNode, itemId: string, isSelected: boolean): boolean {
    let found = false;
    if (parentNode?.children?.length) {
      for (const item of parentNode.children) {
        if (item.artifact_id === itemId) {
          found = true;
          item.$$isSelected = isSelected;
          break;
        }
        found = this.setNodeIsSelectedById(item, itemId, isSelected);
        if (found) {
          break;
        }
      }
    }
    return found;
  }

  setSelectedHierarchicNodes(parentNode: HierarchicNode, fieldParameters: FieldParameters) {
    if (!parentNode) {
      return;
    }
    const array = this.fieldValueService.getFieldValueFromFieldParametersAsArray(fieldParameters);
    if (array.length) {
      const compareFieldName = this.fieldValueService.getIdFieldName(fieldParameters);
      if (compareFieldName) {
        this.setSelectedHierarchicNodesFromArray(parentNode, array, compareFieldName);
      } else {
        this.logger.warn('I have nothing to compare!');
      }
    }
  }

  async getCanAddNew(reference: Reference): Promise<boolean> {
    let res = false;
    if (!reference.add_new_params || reference.add_new_params.can_add_new) {
      const canAddNewRes = await this.cms.getCanAddNew({
        object_type: reference.add_new_params?.new_object_type || reference.object_type
      });
      res = canAddNewRes.can_add_new;
    }
    return res;
  }

  async getDisplayField(reference: Reference, altFieldName: string): Promise<string> {
    return this.getConfigValue(reference?.display_field, altFieldName);
  }

  async getNodeDisplayField(reference: Reference, altFieldName: string) {
    return this.getConfigValue(reference?.node_display_field, altFieldName);
  }

  getArrayObjectAndArrayName(fieldParameters: FieldParameters): [BaseModel, string] {
    const metaField: MetaField = fieldParameters.field;
    let arrayFieldName = metaField.name;
    if (!metaField.inline) {
      return [null, null];
    }
    const inlineFieldName = this.getInlineFieldName(metaField);
    let arrayObject = fieldParameters.object;
    if (inlineFieldName === metaField.name) {
      // This will happen when field is primary field
      arrayFieldName = metaField.path;
      if (fieldParameters.grandParentObject[arrayFieldName]) {
        arrayObject = fieldParameters.grandParentObject
      } else if (fieldParameters.rootObject[arrayFieldName]) {
        arrayObject = fieldParameters.rootObject;
      } else {
        this.logger.warn(`Could not find object containing array named ${arrayFieldName}`);
      }
    }
    return [arrayObject, arrayFieldName];
  }

  private async getConfigValue(fieldName: string, altFieldName: string): Promise<string> {
    return new Promise<string>((resolve, reject) => {
      if (fieldName?.includes('config(')) {
        const configKey = fieldName.split('(')[1].split(')')[0];
        const afterEqual = fieldName.split('=')[1].split(':');
        this.cmsQueue.runCmsFnWithQueue(this.cms.getConfig, {key: configKey}, false,
          configVal => {
            fieldName = configVal.value.toString() === afterEqual[0] ? afterEqual[1] : afterEqual[3];
            resolve(fieldName || altFieldName);
          },
          e => reject(e));
      } else {
        resolve(fieldName || altFieldName);
      }
    });
  }

  private setSelectedHierarchicNodesFromArray(parentNode: HierarchicNode, array: BaseModel[], compareFieldName: string) {
    let i = 0;
    while (array.length && i < array.length && parentNode.children?.length) {
      const arrayItem = array[i];
      let matchIndex = -1;
      for (const child of parentNode.children) {
        if (child.artifact_id === arrayItem[compareFieldName]) {
          child.$$isSelected = true;
          matchIndex = i;
          break;
        }
      }
      if (matchIndex !== -1) {
        array.splice(matchIndex, 1);
      } else {
        i++;
      }
    }
    this.loopParentNodeSetSelectedNodesFromArray(parentNode, array, compareFieldName);
  }

  private loopParentNodeSetSelectedNodesFromArray(parentNode: HierarchicNode, array: BaseModel[], compareFieldName: string) {
    if (array.length && parentNode.children?.length) {
      for (const child of parentNode.children) {
        if (!child.children?.length) {
          continue;
        }
        this.setSelectedHierarchicNodesFromArray(child, array, compareFieldName);
        if (!array.length) {
          break;
        }
      }
    }
  }

  private searchObjectToNode(searchObj: SearchObject, hasParent: boolean, displayField: string): HierarchicNode {
    const node = <HierarchicNode>searchObj;
    node.children = [];
    node.$$hasParent = hasParent;
    node.$$name = searchObj[displayField] || searchObj['name.name'];
    return node;
  }

  private appendNodesAddViewMoreNode(parentNode: HierarchicNode, appendNodes: HierarchicNode[], searchCount?) {
    if (!appendNodes.length) {
      return;
    }
    const nodes = parentNode.children;
    if (nodes.length) {
      const lastNode = nodes.pop();
      if (lastNode.$$nodeType !== 'loadMoreNode') {
        nodes.push(lastNode);
        this.logger.warn('Last node should have been type "loadMoreNode".');
      }
    }
    // Adds new nodes to nodes array
    nodes.push.apply(nodes, appendNodes);
    if (searchCount && nodes.length < searchCount) {
      // Add 'view more' node.
      const loadMoreNode = new HierarchicNode();
      loadMoreNode.$$nodeType = 'loadMoreNode';
      loadMoreNode.$$rows = nodes.length;
      loadMoreNode.$$parentNode = parentNode;
      loadMoreNode.parent_id = nodes[0].parent_id;
      loadMoreNode.level = nodes[0].level;
      nodes.push(loadMoreNode);
    }
  }

  private async putSearchNodesInHierarchy(rootNode: HierarchicNode, searchNodes: HierarchicNode[], displayField: string) {
    let hasMissingParent: boolean;
    let counter = 0;
    do {
      hasMissingParent = false;
      // Add level 1 search nodes to the root node
      this.loopSearchNodesSetHasParent(rootNode, searchNodes);
      // Put orphan nodes into parent nodes
      const nodesCopy = [...searchNodes];
      for (const searchNode of nodesCopy) {
        hasMissingParent = await this.checkSetHasParent(searchNode, searchNodes, displayField, hasMissingParent);
      }
    } while (hasMissingParent && counter++ < 10000);
    if (counter >= 10000) {
      this.logger.warn('Had problems putting nodes in hierarchy');
    }
  }

  private loopSearchNodesSetHasParent(rootNode: HierarchicNode, searchNodes: HierarchicNode[]) {
    for (const searchNode of searchNodes) {
      if (!searchNode.$$hasParent && searchNode.level === 1) {
        rootNode.children.push(searchNode);
        searchNode.$$hasParent = true;
      }
    }
  }

  private async checkSetHasParent(searchNode: HierarchicNode,
                                  searchNodes: HierarchicNode[],
                                  displayField: string,
                                  hasMissingParent: boolean) {
    if (!searchNode.$$hasParent && searchNode.parent_id) {
      hasMissingParent = true;
      // Setting $$hasParent to true even though we may not actually find a parent in order to avoid eternal loop
      searchNode.$$hasParent = true;
      const getParentResult = await this.getParentNode(searchNode, searchNodes);
      if (getParentResult.parentNode) {
        if (!getParentResult.existed) {
          searchNodes.push(this.searchObjectToNode(getParentResult.parentNode, false, displayField));
        }
        getParentResult.parentNode.children.push(searchNode);
      }
    }
    return hasMissingParent;
  }

  // Will either return an existing parent node in existing result set or do a SOLR search for parent id.
  // Parent nodes found using search will be added to the existing search node array.
  private async getParentNode(childNode: HierarchicNode, nodes: HierarchicNode[]): Promise<GetParentResult> {
    let parentNode: HierarchicNode;
    let existed: boolean;
    for (const node of nodes) {
      if (node.artifact_id === childNode.parent_id) {
        parentNode = node;
        existed = true;
        break;
      }
    }
    if (!parentNode) {
      const searchParameters = {} as SearchParameters;
      this.solrFilter.addFq(searchParameters, 'artifact_id', childNode.parent_id);
      const searchResult = await this.searchService.search(searchParameters);
      if (searchResult.artifacts.length) {
        parentNode = <HierarchicNode>searchResult.artifacts[0];
        existed = false;
        this.logger.info('Had to search for parent node');
      } else {
        this.logger.warn(`Parent node with id ${childNode.parent_id} not found`);
      }
    }
    return {parentNode: parentNode, existed: existed};
  }

  // Unable to remove worker file due to project failing to build, thus leaving the worker accessing code commented out
  // private setHierarchyNodes(auth, searchParams: SearchParameters, level: number, callback) {
  //   if (typeof Worker !== 'undefined') {
  //     const worker = new Worker(new URL('./hierarchic-node.worker', import.meta.url), {type: 'module'});
  //     worker.onmessage = ({data}) => {
  //       callback(data);
  //     }
  //     worker.postMessage({
  //       auth: auth,
  //       url: this.cms.getApiUrl('search/json'),
  //       searchParams: searchParams,
  //       level: level
  //     });
  //   }
  // }
  //
  private buildHierarchicQuery(query: string, addWildcards: boolean) {
    let res = query;
    let lastPathSepIdx = res.lastIndexOf('»');
    if (lastPathSepIdx === -1) {
      lastPathSepIdx = res.lastIndexOf(':');
    }
    if (lastPathSepIdx !== -1) {
      res = res.substring(lastPathSepIdx + 2);
    }
    if (addWildcards) {
      res = (res || '') + '*';
    }
    return res;
  }

  private async setSelectedOptionName(reference: Reference, option: Option, query) {
    option.$$name = await this.getTextWithQueryHighlight(reference, option, 'name', query);
  }

  private async getOptionName(reference: Reference, option: Option, addCodeToOption?: boolean): Promise<string> {
    let name;
    const displayField = await this.getDisplayField(reference, 'artifact_name');
    if (addCodeToOption && option.code) {
      name = option.code + ': ' + option['name.name'];
    } else {
      name = (option) ? option[displayField] : undefined;
    }
    if (!name) {
      this.logger.warn('Cannot get option name for ' + JSON.stringify(option));
    }
    return name;
  }

  private async getTextWithQueryHighlight(
    reference: Reference, option: Option, fieldName: string, query: string, addCodeToOption?: boolean) {
    let res;
    if (fieldName === 'name') {
      res = await this.getOptionName(reference, option, addCodeToOption);
    } else {
      res = option[fieldName];
    }
    if (res && query) {
      const myQuery = this.buildHierarchicQuery(query, false);
      if (myQuery.length > 1) {
        res = this.replaceRecursive(res, myQuery);
      }
    }
    if (fieldName === 'name' && option.authority_id_value === 'KulturNav') {
      const markedClass = option.authority_marked === true ? 'marked' : '';
      if (option.m_path) {
        res = '<span class=\'concept-authority-left ' + markedClass + '\'></span>' +
          '<span class=\'concept-name\'>' + res + '</span>';
      } else {
        res = '<span class=\'concept-name\'>' + res + '</span>' +
          '<span class=\' concept-authority-right' + markedClass + '\'></span>';
      }
    }
    if (option['icon']) {
      res = this.getOptionIcon(option) + '<span class=\'concept-name\'>' + res + '</span>';
    }
    return res;
  }

  private getOptionIcon(option: Option) {
    let res = '';
    if (option.icon) {
      res = '<i class="' + option.icon + ' select-options-icon">' + option.icon_frame + '</i>';
    }
    return res;
  }

  private replaceRecursive(text: string, findText: string) {
    let res = text;
    const findIndex = res.toLocaleLowerCase().indexOf(findText.toLocaleLowerCase());
    if (findIndex !== -1) {
      const endQPos = findIndex + findText.length;
      res = res.substring(0, findIndex) + '<strong>' + res.substring(findIndex, endQPos) + '</strong>' +
        this.replaceRecursive(res.substring(endQPos), findText);
    }
    return res;
  }

  private async getReference(fieldParameters: FieldParameters): Promise<Reference> {
    let reference: Reference;
    const parentField = this.getParentField(fieldParameters);
    if (parentField && (parentField.reference_id || parentField.reference)) {
      reference = await this.getReferenceFromParentField(parentField, fieldParameters);
    }
    if (!reference) {
      reference = await this.getReferenceFromField(fieldParameters);
    }
    if (reference) {
      reference = this.getSubstituteReference(fieldParameters, reference);
    }
    return reference;
  }

  private getParentField(fieldParameters: FieldParameters): MetaField {
    let parentField;
    const parentName = fieldParameters.field.parent_name || fieldParameters.field.path;
    if (parentName) {
      if (fieldParameters.grandParentObject) {
        parentField = fieldParameters.grandParentObject.$$meta[parentName];
      }
      if (!parentField) {
        parentField = fieldParameters.rootObject.$$meta[parentName];
      }
      if (!parentField) {
        this.logger.warn('Did not find parent field ' + parentName);
      }
    }
    return parentField;
  }

  private async getReferenceFromParentField(parentField: MetaField, fieldParameters: FieldParameters): Promise<Reference> {
    let reference;
    const parentRef = await this.searchReferenceService.getSearchReferenceFromField(parentField);

    if (parentRef && parentRef.ref_prop) {
      if (parentRef.ref_prop === fieldParameters.field.name) {
        reference = parentRef;
      } else {
        // This warning has to remain until we are sure these changes does not cause other issues
        this.logger.warn(`Parent ref prop ${parentRef.ref_prop} did not match field name ${fieldParameters.field.name}`);
      }
    } else if (parentRef) {
      // This warning has to remain until we are sure these changes does not cause other issues
      this.logger.warn(`No parent reference set for ${fieldParameters.field.name}`);
    }
    return reference;
  }

  private async getReferenceFromField(fieldParameters: FieldParameters): Promise<Reference> {
    const reference = await this.searchReferenceService.getSearchReferenceFromField(fieldParameters.field);
    if (!reference) {
      this.logger.error('Reference info not found for field ' + fieldParameters.field.name);
    }
    return reference;
  }

  private getSubstituteReference(fieldParameters: FieldParameters, origRef: Reference): Reference {
    const reference = new Reference();
    for (let [key, value] of Object.entries(origRef)) {
      let fieldVal = value;
      const substString = this.getSubstitutionString(value);
      if (substString) {
        // Currently, a very simple method for reference value substitution is used here, may need to improve
        // on this if more complex cases occurs
        fieldVal = fieldParameters.rootObject[substString];
        if (fieldVal === undefined) {
          this.logger.warn(`Unable to get substitution value for ${value}`);
        }
      }
      reference[key] = fieldVal;
    }
    return reference;
  }

  private getSubstitutionString(value) {
    let res;
    if (typeof value === 'string') {
      const substStart = value.indexOf('{');
      if (substStart !== -1) {
        const substEnd = value.indexOf('}');
        if (substEnd !== -1) {
          res = value.substring(substStart + 1, substEnd);
        } else {
          this.logger.warn(`Missing bracket end for substitution value ${value}`);
        }
      }
    }
    return res;
  }

  private async getSearchParams(params): Promise<SearchParameters> {
    let parentInfo;
    const options = await this.getSearchOptions(params);
    const reference: Reference = await this.getReference(params.fieldParameters);
    const res = {
      'sort': reference.sort,
      'rows': 200,
      'parents_only': reference.parents_only,
      'write_collections_only': reference.write_collections_only
    } as SearchParameters;
    if (reference.group_search) {
      res.group_search = reference.group_search;
      res.group_field = reference.group_field;
    }
    this.solrFilter.addFq(res, '-valid', false);
    if (reference.object_type) {
      this.solrFilter.addFq(res, 'object_type', reference.object_type);
    }
    if (reference.meta_type) {
      this.solrFilter.addFq(res, 'meta_type', reference.meta_type);
    }
    if (options && options.length) {
      this.solrFilter.addFq(res, 'artifact_id', options);
    }

    parentInfo = await this.getParentInfo(params);
    if (parentInfo) {
      this.solrFilter.addFq(res, parentInfo.parentFilterField, parentInfo.parentId);
    }

    const refFilterData = await this.referenceFilterSvc.generateRefFilterData(
      reference.ref_filter, true, params.fieldParameters);
    if (refFilterData) {
      this.solrFilter.setFqFromObject(res, refFilterData);
    }
    if (params.query) {
      this.solrFilter.addFq(res, 'artifact_name_upper', this.parseQuery(params.query));
    }
    return res;
  }

  private async getSearchOptions(params): Promise<Array<object>> {
    const metaField: MetaField = params.metaField;
    const optionsInfo: OptionInfo = this.valueOptionService.getValueOptionsForField(metaField);
    if (!optionsInfo) {
      return null;
    }
    let options = optionsInfo.options[0].value;
    if (options.includes('user.collections')) {
      const userData = await this.userCacheService.getUserData();
      if (options.includes(':w')) {
        options = [];
        for (const collectionId in userData.collections) {
          if (userData.collections[collectionId].includes('w')) {
            options.push(collectionId);
          }
        }
      } else {
        options = Object.keys(userData.collections);
      }
    }
    return options;
  }

  private async getParentInfo(params) {
    let res = null, ref: Reference;
    const metaField: MetaField = params.metaField;
    let parentId = metaField.parent_id, parentField;
    let parentFilterField = 'parent_id';
    if (!parentId) {
      ref = await this.getReference(params.fieldParameters);
      if (ref && ref.parent_field) {
        parentField = ref.parent_field;
        parentFilterField = ref.parent_target_field || parentFilterField;
        const fieldParameters: FieldParameters = params.fieldParameters;
        if (!parentId && fieldParameters.object) {
          parentId = fieldParameters.object[parentField];
        }
        if (!parentId) {
          const rootObject = fieldParameters.rootObject || fieldParameters.sectionsContainer.rootObject;
          parentId = rootObject[parentField];
        }
      }
    }
    if (parentId) {
      res = {
        parentFilterField: parentFilterField,
        parentId: parentId
      };
    }
    return res;
  }

  private filterUniqueOptions(parentArray, options, uniqueProps, uniqueValues) {
    // TODO: Need to support more than one unique prop
    const uniqueProp = uniqueProps[0];
    for (const item of parentArray) {
      const indexOf = this.getOptionIndex(options, item[uniqueProp]);
      if (indexOf !== -1) {
        if (uniqueValues === undefined) {
          options.splice(indexOf, 1);
        } else {
          if (uniqueValues.indexOf(item[uniqueProp]) !== -1) {
            options.splice(indexOf, 1);
          }
        }
      }
    }
    return options;
  }

  private getOptionIndex(options, value) {
    let t, indexOf = options.indexOf(value);
    if (indexOf === -1) {
      for (t = 0; t < options.length; t++) {
        if (this.getOptionCompareValue(options[t]) === value) {
          indexOf = t;
          break;
        }
      }
    }
    return indexOf;
  }

  private getOptionCompareValue(option: Option) {
    let compVal = option.value;
    if (compVal === undefined) {
      compVal = option.artifact_id;
    }
    if (compVal === undefined) {
      this.logger.warn('Option missing compare value');
    }
    return compVal;
  }

  private async setFieldValueFromOption(option: Option, params: SelectFieldOptionContainer, reference: Reference) {
    const oldFieldValue = this.fieldValueService;
    let fieldValue = option[params.refProp];
    if (reference.get_object_type_from_superobject_type_id) {
      const objectTypes = this.modelsService.getObjectTypesFromSuperObjectTypeIds([option.artifact_id]);
      fieldValue = objectTypes[option.artifact_id];
    }
    this.setFieldValue(fieldValue, params.fieldParameters, params.fieldKey);
    await this.setOthers(option, params.fieldParameters, reference);
    const queryValue: string = await this.getOptionName(reference, option);
    await this.setSelectedOptionName(reference, option, null);
    const temporaryFieldValueName = '$$' + params.fieldParameters.field.name + '_value';
    params.fieldParameters.object[temporaryFieldValueName] = option.$$name;
    params.fieldParameters.object['icon'] = option.icon;
    params.fieldParameters.object['icon_frame'] = option.icon_frame;
    this.setQueryValue({value: queryValue}, params);
    this.setFieldTextValue(queryValue, params.fieldParameters, params.fieldKey);
    if (!oldFieldValue || !reference.is_hierarchic || option.is_leaf) {
      setTimeout(() => {
        params.editFieldSelectQuery.toggleShowOptions(false);
      }, 300);
    }
    // Run a new search on all options so that next time user clicks the fields, all options will be displayed
    return queryValue;
  }

  private async addFieldArrayItemsFromOptions(options: Option[],
                                              params: SelectFieldOptionContainer,
                                              reference: Reference) {
    const array = this.getArray(params.fieldParameters);
    const queryValue = await this.getOptionName(reference, options[0]);
    if (this.fieldValueService.isSingleItemArray(params.fieldParameters)) {
      if (array.length) {
        this.deleteExistingArrayElements(params);
      }
      await this.createAddArrayProps(options, array, params, reference);
      this.setQueryValue({value: queryValue}, params);
      params.editFieldSelectQuery.toggleShowOptions(false);
      return queryValue;
    } else {
      await this.createAddArrayProps(options, array, params, reference);
      params.myFormControl.setValue(array.length ? array.length : '');
      return array.length;
    }
  }

  private async createAddArrayProps(options: Option[], array, params: SelectFieldOptionContainer, reference: Reference) {
    const inline: Inline = params.fieldParameters.field.inline;
    const inlineFieldName = this.getInlineFieldName(params.fieldParameters.field);
    for (const option of options) {
      const data = new BaseModel();
      data[inlineFieldName] = option[params.refProp];
      this.fieldValueService.setValueCompanion(data, inlineFieldName, option.artifact_name).then();
      await this.setSelectedOptionName(reference, option, null);
      params.temporaryFieldValueName = '$$' + inlineFieldName + '_value';
      data[params.temporaryFieldValueName] = option.$$name;
      this.modelFactory.createAddArrayItem(params.fieldParameters.rootObject, array, inline.model, data);
      option.$$isSelected = true;
    }
  }

  private deleteExistingArrayElements(params: SelectFieldOptionContainer) {
    const array = this.getArray(params.fieldParameters);
    for (let t = array.length - 1; t >= 0; t--) {
      this.modelFactory.deleteArrayItem(array, t, params.fieldParameters.rootObject);
    }
  }

  // The reference meta attribute may contain a "set_others" attribute that allows other field values to be set based on the option data
  private async setOthers(option, fieldParameters: FieldParameters, reference: Reference) {
    for (const setOther of reference.set_others || []) {
      const sourceField = setOther.source_field;
      const targetField = setOther.target_field;
      fieldParameters.object[targetField] = option[sourceField];
    }
  }

  private findFieldArrayItemIndex(itemId, params: SelectFieldOptionContainer) {
    const inline = params.fieldParameters.field.inline;
    if (!inline) {
      this.logger.warn('Missing inline attribute');
      return;
    }
    const inlineFieldName = this.getInlineFieldName(params.fieldParameters.field);
    const array = this.getArray(params.fieldParameters);
    let foundIndex = -1;
    array.forEach((item, index) => {
      if (item[inlineFieldName] === itemId) {
        foundIndex = index;
      }
    });
    if (foundIndex === -1) {
      this.logger.warn('Array element with id ' + itemId + ' not found');
    }
    return foundIndex;
  }

  private firstArrayItemNotDeleted(fieldParameters: FieldParameters) {
    const fieldArray = this.getArray(fieldParameters);
    let res;
    for (const item of fieldArray) {
      if (!item._destroy) {
        res = item;
        break;
      }
    }
    return res;
  }

  private setFieldValue(value, fieldParameters: FieldParameters, fieldKey) {
    const sectionsContainer = fieldParameters.sectionsContainer;
    this.fieldValueService.setFieldValue(fieldParameters.rootObject, fieldKey, value);
    const actionParams = new FieldActionParameters();
    actionParams.sectionsContainer = sectionsContainer;
    actionParams.rootObject = sectionsContainer.rootObject;
    actionParams.field = fieldParameters.field;
    actionParams.object = fieldParameters.object;
    actionParams.index = fieldParameters.index;
    actionParams.parentIndex = fieldParameters.parentIndex;
    actionParams.grandParentObject = fieldParameters.grandParentObject;
    actionParams.edit = true;
    actionParams.trigger = 'after_edit';
    this.fieldActionService.runActions(actionParams).then();
  }

  private setFieldTextValue(value, fieldParameters: FieldParameters, fieldKey) {
    this.fieldValueService.setFieldValue(fieldParameters.rootObject, fieldKey, value, true);
  }

  private getTextValue(fieldParameters: FieldParameters): string {
    let res = '';
    if (!this.fieldValueService.isSingleItemArray(fieldParameters)) {
      res = this.fieldValueService.getValueCompanion(fieldParameters.object, fieldParameters.field.name);
    } else {
      const inlineFieldName = this.getInlineFieldName(fieldParameters.field);
      const item = this.firstArrayItemNotDeleted(fieldParameters);
      if (item) {
        res = this.fieldValueService.getValueCompanion(item, inlineFieldName);
      }
    }
    return res;
  }

  private parseQuery(query: string) {
    return this.queryParser.parse(query, true, ' ', '_').toLocaleUpperCase();
  }

  private getInlineFieldName(metaField: MetaField) {
    let res = null;
    if (metaField.inline?.unique_props?.length) {
      res = metaField.inline.unique_props[0];
    } else {
      this.logger.warn(`Missing inline unique props for ${metaField.name}`)
    }
    return res;
  }

  private getArray(params: FieldParameters): BaseModel[] {
    const [arrayObject, arrayName] = this.getArrayObjectAndArrayName(params);
    return arrayObject[arrayName];
  }

  // Used when ref array field is used in primary field context
  private removeEmptyRefArrayItems(fieldParameters: FieldParameters) {
    if (fieldParameters.field.input_type === FieldInputType.REF_ARRAY) {
      let deleteIndex = -1;
      const inlineFieldName = this.getInlineFieldName(fieldParameters.field);
      const array = this.getArray(fieldParameters);
      for (const [index, option] of array.entries()) {
        if (option[inlineFieldName] === null) {
          deleteIndex = index;
        }
      }
      if (deleteIndex !== -1) {
        array.splice(deleteIndex, 1);
      }
    }
  }
}
